import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';

import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, Subscription, filter, map, take } from 'rxjs';

import { ConfigService, pathSegmentsToPathString, SentryLogger, unsubscribe } from '@fcom/core/index';
import {
  Category,
  ControlData,
  FinnairBoundItem,
  FinnairFrequentFlyerCardItem,
  FinnairItineraryItemFlight,
  FinnairOrder,
  FinnairPassengerItem,
  FinnairServiceItem,
} from '@fcom/dapi/api/models';
import { BoundType } from '@fcom/dapi/index';
import { combineServices } from '@fcom/common-booking/modules/ancillaries/utils';
import { getServiceData } from '@fcom/common-booking/utils/ancillaries.utils';
import { isFlight } from '@fcom/common-booking/utils/common-booking.utils';
import { isPassengerFFTierChanged } from '@fcom/manage-booking-common/utils';
import { GaContext, LoaderType } from '@fcom/common/interfaces';
import {
  BookingOrderService,
  CommonBookingFeatureState,
  OrderActions,
  orderDataPrimaryPassenger,
  OrderStatus,
  SeatMapActions,
} from '@fcom/common-booking/index';

import { ManageBookingFeatureState } from '../../../interfaces';
import { ManageBookingService } from '../../../services';
import { CheckInActions } from '../../../store';
import { CheckInStep } from '../../../enums';
import { CheckInService } from '../../../services/check-in.service';
import { getPassengerIdsWithInfants, isNotCarrierConnectBound } from '../../../utils/check-in.utils';

@Component({
  selector: 'fin-check-in-passengers-route',
  templateUrl: 'check-in-passengers-route.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CheckInPassengersRouteComponent implements OnInit, OnDestroy {
  readonly OrderStatus = OrderStatus;
  readonly LoaderType = LoaderType;
  readonly GaContext = GaContext;
  readonly BoundType = BoundType;

  order$: Observable<FinnairOrder>;
  orderStatus$: Observable<OrderStatus>;
  bound$: Observable<FinnairBoundItem>;
  passengers$: Observable<FinnairPassengerItem[]>;
  seats$: Observable<FinnairServiceItem>;
  selectedForCheckInPassengers$: Observable<FinnairPassengerItem[]>;
  controlData$: Observable<ControlData>;
  mainPaxInfo$: Observable<FinnairPassengerItem>;
  allFrequentFlyerCards$: Observable<FinnairFrequentFlyerCardItem[]>;
  clearPendingSeatsOnEntry$: Observable<boolean>;
  loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  modifyCheckIn$: Observable<boolean>;
  clearingSeatsInProgress$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isAPISRequired$: Observable<boolean>;
  nextStep$: Observable<CheckInStep>;

  checkoutUrl: string;

  private subscriptions = new Subscription();

  constructor(
    private store$: Store<CommonBookingFeatureState & ManageBookingFeatureState>,
    private configService: ConfigService,
    private activatedRoute: ActivatedRoute,
    private locationService: Location,
    private bookingOrderService: BookingOrderService,
    private manageBookingService: ManageBookingService,
    private sentryLogger: SentryLogger,
    private checkInService: CheckInService
  ) {}

  ngOnInit(): void {
    this.checkoutUrl = this.configService.cfg.checkoutUrl;
    this.modifyCheckIn$ = this.checkInService.isModifyCheckIn();
    this.clearPendingSeatsOnEntry$ = this.checkInService.getClearPendingSeatsOnEntry();
    this.order$ = this.checkInService.getOrder();
    this.orderStatus$ = this.checkInService.getOrderStatus();
    this.bound$ = this.checkInService.getBound();
    this.isAPISRequired$ = this.checkInService.isAPISRequired();

    this.subscriptions.add(
      combineLatest([this.clearPendingSeatsOnEntry$.pipe(take(1)), this.order$])
        .pipe(take(1))
        .subscribe(([clearPendingSeats, order]) => {
          if (clearPendingSeats) {
            this.clearAllPendingSeats(order);
            this.checkInService.setClearPendingSeatsOnEntry(false);
          } else {
            this.prefetchSeatMaps();
          }
        })
    );
    this.subscriptions.add(
      this.activatedRoute.queryParams
        .pipe(
          take(1),
          filter((parameters) => parameters?.orderid && parameters?.hash)
        )
        .subscribe((routeParams) => {
          this.store$.dispatch(
            OrderActions.setOrderFetchInformation({
              orderInformation: {
                id: routeParams?.orderid,
                hash: routeParams?.hash,
              },
            })
          );

          // Remove sensitive param from history and browser url
          this.locationService.replaceState(pathSegmentsToPathString(this.activatedRoute.snapshot.pathFromRoot));
          this.bookingOrderService.queueOrderLoad(true);
        })
    );

    this.passengers$ = this.order$.pipe(map(({ passengers }) => passengers));
    this.seats$ = this.checkInService.getSeats();
    this.selectedForCheckInPassengers$ = this.checkInService.getPassengers();
    this.controlData$ = this.order$.pipe(map((order) => order.controlData));
    this.mainPaxInfo$ = this.store$.pipe(orderDataPrimaryPassenger());
    this.allFrequentFlyerCards$ = this.order$.pipe(
      map(({ passengers }) => passengers.flatMap((pax) => pax.frequentFlyerCards ?? []))
    );
    this.nextStep$ = this.isAPISRequired$.pipe(
      map((isAPISRequired) => (isAPISRequired ? CheckInStep.APIS : CheckInStep.SEATMAP))
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  confirmPassengersForCheckIn(passengerIds: string[]): void {
    this.subscriptions.add(
      combineLatest([
        this.passengers$,
        this.checkInService.isExitRowSeatConfirmationRequiredForPassengers(passengerIds),
        this.nextStep$,
      ])
        .pipe(take(1))
        .subscribe(([passengers, exitSeatConfirmationRequired, nextStep]) => {
          this.store$.dispatch(
            CheckInActions.setPassengers({ passengers: getPassengerIdsWithInfants(passengers, passengerIds) })
          );
          if (exitSeatConfirmationRequired && nextStep === CheckInStep.SEATMAP) {
            this.checkInService.setShowExitRowSeatsNotification(true);
          } else {
            this.checkInService.triggerNextCheckInStep();
          }
        })
    );
  }

  prefetchSeatMaps(oldPassenger?: FinnairPassengerItem): void {
    this.subscriptions.add(
      combineLatest([this.order$, this.bound$])
        .pipe(
          take(1),
          filter(
            ([order, bound]) =>
              isNotCarrierConnectBound(order, bound) &&
              order.otherInformation.featureFlags?.checkInSeatsEnabled === true &&
              isPassengerFFTierChanged(order, oldPassenger)
          )
        )
        .subscribe(([order, checkInBound]) => {
          checkInBound.itinerary.filter(isFlight).forEach((flight) => {
            this.manageBookingService.triggerFetchSeatMap(flight, order, true);
          });
        })
    );
  }

  private clearAllPendingSeats(order: FinnairOrder) {
    const seats = combineServices([order?.services?.pending || [], order?.services?.unpaid || []]).filter(
      (s) => s.category === Category.SEAT
    );
    if (seats.length > 0) {
      this.clearingSeatsInProgress$.next(true);
      seats
        .flatMap((service) => service.bounds)
        .flatMap((bound) => bound.segments)
        .forEach((segment) => {
          const flight: FinnairItineraryItemFlight = order.bounds
            .flatMap((b) => b.itinerary)
            .filter(isFlight)
            .find((f) => f.id === segment.id);
          segment.passengers.forEach((passenger) => {
            this.store$.dispatch(
              SeatMapActions.setSeatForFlight({
                flightId: flight.id,
                travelerId: passenger.id,
                seat: {},
              })
            );
          });
          const [requests] = getServiceData(this.store$, Category.SEAT, order.bounds, [flight]);
          const didOperation = this.manageBookingService.setServices(requests, Category.SEAT, {
            next: () => {
              this.clearingSeatsInProgress$.next(false);
              this.prefetchSeatMaps();
            },
            error: (error) => {
              this.clearingSeatsInProgress$.next(false);
              this.sentryLogger.warn('Could not clear pending seats when entering CHECK-IN', {
                orderId: order.id,
                error: error,
              });
              this.prefetchSeatMaps();
            },
          });
          if (!didOperation) {
            this.clearingSeatsInProgress$.next(false);
            this.prefetchSeatMaps();
          }
        });
    } else {
      this.prefetchSeatMaps();
    }
  }
}
